import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, Accordion, Badge } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { questionnaireService } from '../../services/questionnaireService';
import { IFullQuestionnaire, IQuestion } from '../../interfaces/questionnaire';
import ConfirmationModal from '../../components/ConfirmationModal';
import api from '../../services/api';
import { questionService } from '../../services/questionService';
import { frameworkService } from '../../services/frameworkService';
import { IFramework } from '../../interfaces/framework';
import ChecklistBuilder from '../../components/ChecklistBuilder';
import { checklistService } from '../../services/checklistService';
import PaginatedTable from '../../components/PaginatedTable';
import DomainTreeSelector from '../../components/DomainTreeSelector';
import { notify } from '../../stores/useNotificationStore';

const QuestionnairePage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [questionnaires, setQuestionnaires] = useState<IFullQuestionnaire[]>([]);
    const [newQuestionnaireName, setNewQuestionnaireName] = useState('');
    const [newQuestionnaireDescription, setNewQuestionnaireDescription] = useState('');
    const [isModel, setIsModel] = useState(false);
    const [frameworks, setFrameworks] = useState<IFramework[]>([]);
    const [framework, setFramework] = useState<string>('');
    const [dominioId, setDominioId] = useState<number | ''>('');
    const [tipoResposta, setTipoResposta] = useState<'TEXTO_LIVRE' | 'SIM_NAO' | 'PERCENTUAL' | 'MULTIPLA_ESCOLHA'>('TEXTO_LIVRE');
    const [loading, setLoading] = useState(false);
    const [perguntas, setPerguntas] = useState<IQuestion[]>([]);
    const [perguntaSelecionada, setPerguntaSelecionada] = useState<number | ''>('');
    const [checklistRequisitos, setChecklistRequisitos] = useState<any[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [questionnaireToDelete, setQuestionnaireToDelete] = useState<IFullQuestionnaire | null>(null);
    const [editingQuestionnaire, setEditingQuestionnaire] = useState<IFullQuestionnaire | null>(null);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            questionnaireService.getAll(),
            frameworkService.getAll(),
            questionService.getAll(),
        ]).then(async ([qData, fData, pData]) => {
            setQuestionnaires(qData);
            setFrameworks(fData);
            setPerguntas(pData);
            
            // Se houver ID na URL, carrega o questionário para edição
            if (id) {
                try {
                    const questionnaireToEdit = qData.find(q => q.id === Number(id));
                    if (questionnaireToEdit) {
                        setEditingQuestionnaire(questionnaireToEdit);
                        // Preenche os campos com os dados do questionário
                        setNewQuestionnaireName(questionnaireToEdit.nome);
                        setNewQuestionnaireDescription(questionnaireToEdit.descricao || '');
                        setIsModel(questionnaireToEdit.is_modelo || false);
                        setFramework(questionnaireToEdit.framework || '');
                        setDominioId(Number(questionnaireToEdit.dominio_id) || '');
                        setPerguntaSelecionada(Number(questionnaireToEdit.pergunta_principal) || '');
                        setTipoResposta(questionnaireToEdit.tipo_resposta || 'TEXTO_LIVRE');
                    }
                } catch (error) {
                    console.error('Erro ao carregar questionário para edição:', error);
                    notify.error('Erro ao carregar questionário para edição.');
                }
            }
        }).catch(err => console.error('Erro ao carregar dados iniciais:', err))
        .finally(() => setLoading(false));
    }, [id]);

    // Função para verificar se o questionário tem perguntas associadas
    const hasQuestions = (item: IFullQuestionnaire) => {
        // Verifica se o questionário tem domínios com perguntas
        return item.dominios && item.dominios.some(d => d.perguntas && d.perguntas.length > 0);
    };

    const handleAddQuestionnaire = async () => {
        if (!newQuestionnaireName.trim() || !framework.trim() || !dominioId || !perguntaSelecionada) {
            notify.warning('Por favor, preencha todos os campos obrigatórios (*).');
            return;
        }
        setLoading(true);
        try {
            const created = await questionnaireService.create({
                nome: newQuestionnaireName,
                descricao: newQuestionnaireDescription,
                is_modelo: isModel,
                empresa_id: 1,
                framework: framework,
                dominio_id: Number(dominioId),
                pergunta_principal: perguntaSelecionada.toString(),
                tipo_resposta: tipoResposta
            });
            if (created) {
                if (checklistRequisitos.length > 0) {
                    await checklistService.createChecklist({
                        descricao: `Checklist para ${newQuestionnaireName}`,
                        requisitos: checklistRequisitos
                    });
                }
                setQuestionnaires(prev => [...prev, created]);
                // Reset form
                setNewQuestionnaireName(''); setNewQuestionnaireDescription(''); setFramework('');
                setDominioId(''); setPerguntaSelecionada('');
                setChecklistRequisitos([]); setTipoResposta('TEXTO_LIVRE'); setIsModel(false);
                notify.success('Questionário criado com sucesso!');
            }
        } catch (error) {
            console.error('Erro ao criar questionário:', error);
            notify.error('Erro ao criar questionário.');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateQuestionnaire = async () => {
        if (!editingQuestionnaire) return;
        
        if (!newQuestionnaireName.trim() || !framework.trim() || !dominioId || !perguntaSelecionada) {
            notify.warning('Por favor, preencha todos os campos obrigatórios (*).');
            return;
        }
        setLoading(true);
        try {
            const updated = await questionnaireService.update(editingQuestionnaire.id!, {
                id: editingQuestionnaire.id!,
                nome: newQuestionnaireName,
                descricao: newQuestionnaireDescription,
                is_modelo: isModel,
                framework: framework,
                dominio_id: Number(dominioId),
                pergunta_principal: perguntaSelecionada.toString(),
                tipo_resposta: tipoResposta
            });
            if (updated) {
                setQuestionnaires(prev => prev.map(q => q.id === editingQuestionnaire.id ? updated : q));
                // Reset form e modo de edição
                setEditingQuestionnaire(null);
                setNewQuestionnaireName(''); setNewQuestionnaireDescription(''); setFramework('');
                setDominioId(''); setPerguntaSelecionada('');
                setChecklistRequisitos([]); setTipoResposta('TEXTO_LIVRE'); setIsModel(false);
                notify.success('Questionário atualizado com sucesso!');
                navigate('/app/questionarios');
            }
        } catch (error) {
            console.error('Erro ao atualizar questionário:', error);
            notify.error('Erro ao atualizar questionário.');
        } finally {
            setLoading(false);
        }
    };

    const handleCloneQuestionnaire = async (originalId: number, originalName: string): Promise<IFullQuestionnaire | null> => {
        const cloneName = prompt('Digite o nome para o questionário clonado:', `${originalName} - Cópia`);
        if (!cloneName) return null;
        const original = questionnaires.find(q => q.id === originalId);
        if (!original) return null;
        setLoading(true);
        try {
            const cloned = await questionnaireService.clone(originalId, {
                nome: cloneName,
                empresa_id: 1,
                framework: original.framework || '',
                dominio_id: Number(original.dominio_id) || undefined,
                pergunta_principal: original.pergunta_principal || '',
                tipo_resposta: original.tipo_resposta || undefined
            });
            if (cloned) {
                await api.clearQuestionnaireAnswers(cloned.id);
                setQuestionnaires(prev => [...prev, cloned]);
                notify.success('Questionário clonado com sucesso! As respostas foram limpas.');
                return cloned;
            }
        } catch (error) {
            console.error('Erro ao clonar questionário:', error);
            notify.error('Erro ao clonar questionário.');
        } finally {
            setLoading(false);
        }
        return null;
    };

    const handleEditQuestionnaire = (item: IFullQuestionnaire) => {
        navigate(`/app/questionarios/editar/${item.id}`);
    };

    const handleRespondToQuestionnaire = async (item: IFullQuestionnaire) => {
        if (item.is_modelo) {
            notify.warning('Modelos de questionário não podem ser respondidos diretamente. Por favor, clone o questionário primeiro.');
            return;
        }
        navigate(`/app/questionario/responder/${item.id}`);
    };

    const handleDeleteQuestionnaire = (q: IFullQuestionnaire) => {
        setQuestionnaireToDelete(q);
        setShowDeleteModal(true);
    };

    const confirmDeleteQuestionnaire = async () => {
        if (!questionnaireToDelete) return;
        setLoading(true);
        try {
            const success = await questionnaireService.delete(questionnaireToDelete.id);
            if (success) {
                setQuestionnaires(questionnaires.filter(q => q.id !== questionnaireToDelete.id));
                notify.success('Questionário excluído com sucesso!');
            }
        } catch (error) {
            notify.error('Erro ao excluir questionário.');
        } finally {
            setShowDeleteModal(false);
            setQuestionnaireToDelete(null);
            setLoading(false);
        }
    };

    const cancelDeleteQuestionnaire = () => {
        setShowDeleteModal(false);
        setQuestionnaireToDelete(null);
    };

    return (
        <>
            <div className="mb-4">
                <h2>{editingQuestionnaire ? 'Edição de Questionário' : 'Cadastro de Questionários'}</h2>
            </div>

            {!editingQuestionnaire ? (
                <Accordion className="mb-4">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Novo Questionário</Accordion.Header>
                        <Accordion.Body>
                            <Form>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group controlId="formFramework">
                                            <Form.Label>Framework *</Form.Label>
                                            <Form.Select value={framework} onChange={(e) => setFramework(e.target.value)}>
                                                <option value="">Selecione...</option>
                                                {frameworks.map(f => <option key={f.id} value={f.nome}>{f.nome}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId="formDominio">
                                            <Form.Label>Domínio de Controle *</Form.Label>
                                            <DomainTreeSelector
                                                empresaId={1} // TODO: Obter dinamicamente
                                                selectedValue={dominioId.toString()}
                                                onValueChange={(value) => setDominioId(Number(value))}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group controlId="formPergunta">
                                            <Form.Label>Pergunta Principal *</Form.Label>
                                            <Form.Select value={perguntaSelecionada} onChange={(e) => setPerguntaSelecionada(Number(e.target.value))}>
                                                <option value="">Selecione uma pergunta</option>
                                                {perguntas.map(p => <option key={p.id} value={p.id}>{p.pergunta}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId="formTipoResposta">
                                            <Form.Label>Tipo de Resposta *</Form.Label>
                                            <Form.Select value={tipoResposta} onChange={(e) => setTipoResposta(e.target.value as any)}>
                                                <option value="TEXTO_LIVRE">Texto Livre</option>
                                                <option value="SIM_NAO">Sim/Não</option>
                                                <option value="PERCENTUAL">Porcentagem</option>
                                                <option value="MULTIPLA_ESCOLHA">Múltipla Escolha</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId="formNome">
                                            <Form.Label>Nome do Questionário *</Form.Label>
                                            <Form.Control type="text" value={newQuestionnaireName} onChange={(e) => setNewQuestionnaireName(e.target.value)} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group controlId="formDescricao">
                                            <Form.Label>Descrição</Form.Label>
                                            <Form.Control as="textarea" rows={3} value={newQuestionnaireDescription} onChange={(e) => setNewQuestionnaireDescription(e.target.value)} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <hr />
                                <ChecklistBuilder requisitos={checklistRequisitos} onRequisitosChange={setChecklistRequisitos} />
                                <hr />
                                <Form.Check type="switch" id="isModelCheckbox" label="É um modelo?" checked={isModel} onChange={(e) => setIsModel(e.target.checked)} className="my-3" />
                                <Button variant="primary" onClick={handleAddQuestionnaire} disabled={loading}>Adicionar Questionário</Button>
                            </Form>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            ) : (
                <Accordion className="mb-4">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>Editar Questionário</Accordion.Header>
                        <Accordion.Body>
                            <Form>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <Form.Group controlId="formFramework">
                                            <Form.Label>Framework *</Form.Label>
                                            <Form.Select value={framework} onChange={(e) => setFramework(e.target.value)}>
                                                <option value="">Selecione...</option>
                                                {frameworks.map(f => <option key={f.id} value={f.nome}>{f.nome}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId="formDominio">
                                            <Form.Label>Domínio de Controle *</Form.Label>
                                            <DomainTreeSelector
                                                empresaId={1} // TODO: Obter dinamicamente
                                                selectedValue={dominioId.toString()}
                                                onValueChange={(value) => setDominioId(Number(value))}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group controlId="formPergunta">
                                            <Form.Label>Pergunta Principal *</Form.Label>
                                            <Form.Select value={perguntaSelecionada} onChange={(e) => setPerguntaSelecionada(Number(e.target.value))}>
                                                <option value="">Selecione uma pergunta</option>
                                                {perguntas.map(p => <option key={p.id} value={p.id}>{p.pergunta}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId="formTipoResposta">
                                            <Form.Label>Tipo de Resposta *</Form.Label>
                                            <Form.Select value={tipoResposta} onChange={(e) => setTipoResposta(e.target.value as any)}>
                                                <option value="TEXTO_LIVRE">Texto Livre</option>
                                                <option value="SIM_NAO">Sim/Não</option>
                                                <option value="PERCENTUAL">Porcentagem</option>
                                                <option value="MULTIPLA_ESCOLHA">Múltipla Escolha</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group controlId="formNome">
                                            <Form.Label>Nome do Questionário *</Form.Label>
                                            <Form.Control type="text" value={newQuestionnaireName} onChange={(e) => setNewQuestionnaireName(e.target.value)} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group controlId="formDescricao">
                                            <Form.Label>Descrição</Form.Label>
                                            <Form.Control as="textarea" rows={3} value={newQuestionnaireDescription} onChange={(e) => setNewQuestionnaireDescription(e.target.value)} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <hr />
                                <ChecklistBuilder requisitos={checklistRequisitos} onRequisitosChange={setChecklistRequisitos} />
                                <hr />
                                <Form.Check type="switch" id="isModelCheckbox" label="É um modelo?" checked={isModel} onChange={(e) => setIsModel(e.target.checked)} className="my-3" />
                                <Button variant="primary" onClick={handleUpdateQuestionnaire} disabled={loading}>Atualizar Questionário</Button>
                                <Button variant="secondary" onClick={() => { setEditingQuestionnaire(null); navigate('/app/questionarios'); }} className="ms-2">Cancelar</Button>
                            </Form>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            )}

            {!editingQuestionnaire && (
                <>
                    <h3 className="mb-3">Questionários Cadastrados</h3>
                    <PaginatedTable<IFullQuestionnaire>
                        data={questionnaires}
                        columns={[
                            { key: 'nome', title: 'Nome', render: (value, item) => <>{value} {item.is_modelo && <Badge bg="info">Modelo</Badge>}</> },
                            { key: 'framework', title: 'Framework' },
                            { key: 'dominio_id', title: 'Domínio' },
                            { key: 'pergunta_principal', title: 'Pergunta Principal', render: (value) => perguntas.find(p => p.id === Number(value))?.pergunta || `ID: ${value}` },
                        ]}
                        onDelete={handleDeleteQuestionnaire}
                        onEdit={handleEditQuestionnaire}
                        onClone={(item) => handleCloneQuestionnaire(item.id, item.nome)}
                        onRespond={handleRespondToQuestionnaire}
                        hasQuestions={hasQuestions}
                    />
                </>
            )}

            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={cancelDeleteQuestionnaire}
                title="Confirmar Exclusão"
                message={`Tem certeza que deseja excluir o questionário "${questionnaireToDelete?.nome}"? Esta ação não pode ser desfeita.`}
                onConfirm={confirmDeleteQuestionnaire}
                onCancel={cancelDeleteQuestionnaire}
                confirmText="Excluir"
                isLoading={loading}
            />
        </>
    );
};

export default QuestionnairePage;